import { useState } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { v4 as uuidv4 } from 'uuid';

import {
  ContentStepWritable,
  getManagedPlansQueryKey,
  getPlanAndAssignmentsQueryKey,
  patchPlanName,
  patchPlanNameMutation,
  Plan,
  PlanUpdateRequestWritable,
  SectionWritable,
  updatePlanMutation,
} from '@cfa/pathway-fe-shared/xp';
import { zBadRequestError } from '@cfa/pathway-fe-shared/xp-zod';

import { extractErrorMessage, extractStatusCode } from '@/utils/error-utils';
import {
  invalidateAllPlanQueries,
  invalidateCertificateQueries,
} from '@/utils/queryInvalidation';

import Constants from '@/constants';

import { queryClient } from '@/services';

import { getMissingFields } from './put-missing-fields';

type PlanUpdateFields = PlanUpdateRequestWritable & {
  allowTrainTogether?: boolean;
  autoAssign?: boolean;
  dueDateOffsetInDays?: number;
};

type LocalizedText = PlanUpdateFields['name'];

// Distributes so each union member keeps its own discriminant.
type WithoutId<T> = T extends unknown ? Omit<T, 'id'> : never;

/** A step as supplied to `addSteps`, which assigns the plan-unique `id`. */
type NewStep = WithoutId<ContentStepWritable>;

const getErrorBody = (error: unknown): unknown => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const errorObj = error as Record<string, unknown>;
  if (errorObj.body !== undefined) {
    return errorObj.body;
  }

  if (typeof errorObj.response === 'object' && errorObj.response !== null) {
    const response = errorObj.response as Record<string, unknown>;
    return response.data ?? response;
  }

  return errorObj;
};

const parseBadRequestError = (error: unknown) => {
  const body = getErrorBody(error);
  if (body === undefined) {
    return undefined;
  }

  const parsed = zBadRequestError.safeParse(body);
  return parsed.success ? parsed.data : undefined;
};

const getPlanUpdateErrorToastMessage = (error: unknown): string => {
  const badRequest = parseBadRequestError(error);
  const violationMessages = (
    badRequest?.violations?.map(violation => violation.message) ?? []
  ).filter((message): message is string => Boolean(message));

  if (violationMessages.length > 0) {
    return violationMessages.join(' ');
  }

  if (badRequest?.message) {
    return badRequest.message;
  }

  return extractErrorMessage(error, 'Failed to update plan');
};

const isSameLocalizedText = (
  a: LocalizedText,
  b: LocalizedText | undefined,
) => {
  if (!b) return false;
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  return aKeys.length === bKeys.length && aKeys.every(key => a[key] === b[key]);
};

/** Update cached plan name without refetching managed plans (avoids unmounting translations UI). */
const patchCachedPlanName = (planId: string, name: LocalizedText) => {
  queryClient.setQueriesData<Plan[]>(
    { queryKey: getManagedPlansQueryKey() },
    plans =>
      plans?.map(plan => (plan.id === planId ? { ...plan, name } : plan)),
  );

  queryClient.setQueriesData<{ plan?: Plan }>(
    { queryKey: getPlanAndAssignmentsQueryKey({ path: { planId } }) },
    cached =>
      cached?.plan ? { ...cached, plan: { ...cached.plan, name } } : cached,
  );
};

const buildPayload = (
  sourcePlan: Plan,
  updates: Partial<PlanUpdateFields>,
): PlanUpdateFields => {
  const sourceStepIds = new Set(
    [
      ...(sourcePlan.content?.steps ?? []),
      ...(sourcePlan.content?.sections?.flatMap(s => s.steps ?? []) ?? []),
    ].map(step => step.id),
  );

  const validateStep = (step: ContentStepWritable) => {
    if (step.type !== Constants.STEP_TYPES.DOCUMENT) return;
    if (step.reference || sourceStepIds.has(step.id)) return;

    const err = new Error(
      'CRITICAL: Document step reached API payload without reference - upstream validation failed',
      { cause: step },
    );
    datadogRum.addError(err);
    throw err;
  };

  const sections: SectionWritable[] | undefined =
    updates.content?.sections ?? sourcePlan.content?.sections;
  const steps: ContentStepWritable[] | undefined =
    updates.content?.steps ?? sourcePlan.content?.steps;

  steps?.forEach(validateStep);
  sections?.forEach(s => s.steps?.forEach(validateStep));

  // Document steps come back from GET carrying an enriched `resource` object.
  // Strip it before PUT — the API reads `reference`, not the hydrated resource.
  const normalizeStep = (step: ContentStepWritable): ContentStepWritable => {
    if (step.type !== Constants.STEP_TYPES.DOCUMENT) return step;
    const { resource: _resource, ...rest } = step;
    return rest;
  };

  const normalizedContent =
    sections !== undefined || steps !== undefined
      ? {
          content: {
            sections: sections?.map(s => ({
              ...s,
              steps: s.steps?.map(normalizeStep),
            })),
            steps: steps?.map(normalizeStep),
          },
        }
      : {};

  // Seed only writable PUT fields. autoAssign / dueDateOffsetInDays /
  // allowTrainTogether are no longer on PlanUpdateRequestWritable (use
  // patchAutoAssign and related endpoints instead). Spreading the full GET
  // Plan would also echo server-owned fields on every PUT.
  const writableBase: PlanUpdateFields = {
    name: sourcePlan.name,
    category: sourcePlan.category,
    description: sourcePlan.description,
    estimatedTimeToCompleteInMinutes:
      sourcePlan.estimatedTimeToCompleteInMinutes,
  };

  return {
    ...writableBase,
    ...updates,
    ...normalizedContent,
  };
};

interface UseUpdatePlanOptions {
  onSuccess?: () => void | Promise<void>;
}

/**
 * Hook for plan mutations. Accepts Plan and PlanUpdateRequestWritable directly.
 *
 * @example
 * const { addSteps, updateName, isPending } = useUpdatePlan();
 * await addSteps(plan, newSteps, sectionId);
 */
export const useUpdatePlan = (options?: UseUpdatePlanOptions) => {
  const updatePlanMutationOptions = updatePlanMutation();
  const patchPlanNameMutationOptions = patchPlanNameMutation();
  // Tracks the mid-save SDK PATCH that bypasses React Query so callers still
  // see isPending during that round-trip (guards concurrent PUTs that would
  // otherwise seed a stale plan.name).
  const [quietNamePatchPending, setQuietNamePatchPending] = useState(false);

  const refreshPlanQueries = async (planId: string) => {
    await Promise.all([
      invalidateAllPlanQueries(planId),
      invalidateCertificateQueries(planId),
    ]);
  };

  const invalidatePlan = async (planId: string) => {
    await refreshPlanQueries(planId);
    await options?.onSuccess?.();
  };

  const putPlanMutation = useMutation({
    mutationKey: updatePlanMutationOptions.mutationKey,
    mutationFn: updatePlanMutationOptions.mutationFn,
    onSuccess: async (_data, variables) => {
      await invalidatePlan(variables.path.planId);
    },
    onError: (error: unknown) => {
      const message = extractErrorMessage(error, 'Failed to update plan');
      const status = extractStatusCode(error);
      datadogRum.addError(
        error instanceof Error ? error : new Error(message, { cause: error }),
        { status },
      );
      if (status === 400) {
        toast.error(getPlanUpdateErrorToastMessage(error));
      }
    },
  });

  const patchNameMutation = useMutation({
    mutationKey: patchPlanNameMutationOptions.mutationKey,
    mutationFn: patchPlanNameMutationOptions.mutationFn,
    onSuccess: async (_data, variables) => {
      await invalidatePlan(variables.path.planId);
    },
    onError: (error: unknown) => {
      const message = extractErrorMessage(error, 'Failed to update plan name');
      const status = extractStatusCode(error);
      datadogRum.addError(
        error instanceof Error ? error : new Error(message, { cause: error }),
        { status },
      );
    },
  });

  const putPlan = async (plan: Plan, updates: Partial<PlanUpdateFields>) => {
    const payload = buildPayload(plan, updates);
    // Should we require more call sites, convert this to a PUT interceptor that does a regex to narrow it by request.
    const missingFields = getMissingFields(payload);
    if (missingFields.length > 0) {
      datadogLogs.logger.warn('put_content_missing_fields', {
        endpoint: `/plans/${plan.id}`,
        missingFields,
        payloadSize: JSON.stringify(payload).length,
        sessionId: datadogRum.getInternalContext()?.session_id,
      });
    }
    return putPlanMutation.mutateAsync({
      path: { planId: plan.id },
      body: payload,
    });
  };

  const addSteps = (plan: Plan, newSteps: NewStep[], sectionId?: string) => {
    // Any incoming step.id may be a resource/document ID which is not unique
    // when the same resource is added to a plan multiple times.
    const stepsWithIds = newSteps.map(
      (step): ContentStepWritable => ({ ...step, id: uuidv4() }),
    );

    return putPlan(
      plan,
      sectionId
        ? {
            content: {
              steps: plan.content?.steps,
              sections: plan.content?.sections?.map(section =>
                section.id === sectionId
                  ? {
                      ...section,
                      steps: [...(section.steps ?? []), ...stepsWithIds],
                    }
                  : section,
              ),
            },
          }
        : {
            content: {
              steps: [...(plan.content?.steps ?? []), ...stepsWithIds],
              sections: plan.content?.sections,
            },
          },
    );
  };

  const addSection = (plan: Plan, section: SectionWritable) =>
    putPlan(plan, {
      content: {
        sections: [...(plan.content?.sections ?? []), section],
      },
    });

  const replaceStepResource = (
    plan: Plan,
    stepId: string,
    replacement: { name: Record<string, string>; reference?: string },
    sectionId?: string,
  ) => {
    const patchStep = (step: ContentStepWritable): ContentStepWritable =>
      step.id === stepId ? { ...step, ...replacement } : step;

    return putPlan(plan, {
      content: sectionId
        ? {
            steps: plan.content?.steps,
            sections: plan.content?.sections?.map(section =>
              section.id === sectionId
                ? { ...section, steps: section.steps?.map(patchStep) }
                : section,
            ),
          }
        : {
            steps: plan.content?.steps?.map(patchStep),
            sections: plan.content?.sections,
          },
    });
  };

  const updateContent = (plan: Plan, content: PlanUpdateFields['content']) =>
    putPlan(plan, { content });

  const updateName = (plan: Plan, name: PlanUpdateFields['name']) =>
    patchNameMutation.mutateAsync({
      path: { planId: plan.id },
      body: { name },
    });

  /**
   * PATCH the name without running the mutation's onSuccess/invalidate.
   * Used as the first half of a multi-write save so a mid-flight refetch
   * cannot unmount the translations modal before the follow-up PUT finishes.
   */
  const patchNameWithoutInvalidate = async (
    planId: string,
    name: LocalizedText,
  ) => {
    setQuietNamePatchPending(true);
    try {
      const { data } = await patchPlanName({
        path: { planId },
        body: { name },
        throwOnError: true,
      });
      return data;
    } catch (error: unknown) {
      const message = extractErrorMessage(error, 'Failed to update plan name');
      const status = extractStatusCode(error);
      datadogRum.addError(
        error instanceof Error ? error : new Error(message, { cause: error }),
        { status },
      );
      throw error;
    } finally {
      setQuietNamePatchPending(false);
    }
  };

  const updateDescription = (
    plan: Plan,
    description: PlanUpdateFields['description'],
  ) => putPlan(plan, { description });

  const updateTranslations = async (
    plan: Plan,
    translations: {
      name?: PlanUpdateFields['name'];
      description?: PlanUpdateFields['description'];
      sections?: SectionWritable[];
      steps?: ContentStepWritable[];
    },
  ) => {
    // Only PATCH the name when the caller actually changed it. Re-patching an
    // unchanged name is a redundant write and widens the split-write window
    // where the name succeeds but the follow-up content/description PUT fails.
    const nextName = translations.name;
    const nameChanged =
      nextName !== undefined && !isSameLocalizedText(nextName, plan.name);

    const hasDescription = translations.description !== undefined;
    const hasContent =
      translations.sections !== undefined || translations.steps !== undefined;
    const needsPut = hasDescription || hasContent;
    let quietNamePatchSucceeded = false;

    if (nameChanged) {
      // When a PUT follows, skip the PATCH onSuccess invalidate — otherwise the
      // managed-plans refetch remounts PlanCards and unmounts translations UI on PlanCards
      // while the PUT is still in flight (a failed PUT would then leave the
      // name saved, the modal gone, and in-progress edits discarded).
      if (needsPut) {
        await patchNameWithoutInvalidate(plan.id, nextName);
        quietNamePatchSucceeded = true;
      } else {
        await patchNameMutation.mutateAsync({
          path: { planId: plan.id },
          body: { name: nextName },
        });
      }
    }

    if (!needsPut) {
      return;
    }

    // PUT always seeds `name` from the source plan. If we already PATCHed (or
    // the caller supplied a name map), pass that map here so the PUT cannot
    // clobber es/other locales with the stale pre-PATCH plan.name.
    try {
      return await putPlan(plan, {
        ...(nextName !== undefined ? { name: nextName } : {}),
        description: translations.description ?? plan.description,
        content: {
          sections: translations.sections,
          steps: translations.steps,
        },
      });
    } catch (error) {
      // Name may already be saved. Patch caches in place so the UI reflects
      // the PATCH without refetching managed plans (which would unmount
      // translations UI on PlanCards mid-error the same way invalidate would).
      if (quietNamePatchSucceeded && nextName !== undefined) {
        patchCachedPlanName(plan.id, nextName);
      }
      throw error;
    }
  };

  const updateEstimatedTime = (
    plan: Plan,
    estimatedTimeToCompleteInMinutes: number,
  ) => putPlan(plan, { estimatedTimeToCompleteInMinutes });

  const updateCategory = (plan: Plan, category: string) =>
    putPlan(plan, { category });

  const deleteStep = (plan: Plan, stepId: string, sectionId?: string | null) =>
    putPlan(plan, {
      content: sectionId
        ? {
            sections: plan.content?.sections?.map(section =>
              section.id === sectionId
                ? {
                    ...section,
                    steps: section.steps?.filter(step => step.id !== stepId),
                  }
                : section,
            ),
          }
        : {
            steps: plan.content?.steps?.filter(step => step.id !== stepId),
          },
    });

  const deleteSection = (plan: Plan, sectionId: string) =>
    putPlan(plan, {
      content: {
        sections: plan.content?.sections?.filter(s => s.id !== sectionId),
      },
    });

  const renameSection = (
    plan: Plan,
    sectionId: string,
    name: PlanUpdateFields['name'],
  ) =>
    putPlan(plan, {
      content: {
        sections: plan.content?.sections?.map(section =>
          section.id === sectionId ? { ...section, name } : section,
        ),
      },
    });

  const isPending =
    putPlanMutation.isPending ||
    patchNameMutation.isPending ||
    quietNamePatchPending;
  const isError = putPlanMutation.isError || patchNameMutation.isError;
  const error = putPlanMutation.error ?? patchNameMutation.error;

  return {
    addSteps,
    addSection,
    deleteStep,
    deleteSection,
    renameSection,
    replaceStepResource,
    updateContent,
    updateCategory,
    updateDescription,
    updateEstimatedTime,
    updateTranslations,
    updateName,
    isPending,
    isError,
    error,
  };
};
